import { UpdateRequest } from 'api/types'
import { diff } from 'deep-object-diff'
import { configActions, estimateActions } from 'ducks/actions'
import { useAppDispatch } from 'ducks/hooks'
import { EstimateContact, EstimateProperties, PunchlistFile } from 'ducks/types'
import {
  APPROVAL_TYPE,
  CONTACT_ROLE,
  FILE_TYPE,
  companyRoles,
  getContactRoleOpt,
  getDifferenceBetweenObjects,
  history,
  isEmpty,
  objectToUpdateRequest,
  validateEmail,
  validatePhone
} from 'helpers/index'
import { FC, useEffect, useState } from 'react'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { useSelector } from 'react-redux'
import {
  getApprovalStep,
  getApprovalValues,
  getContactInEstimate,
  getContactIndexInEstimate,
  getEstimate,
  getTerritory,
  getUser,
  getUserPrimaryEmail
} from '../../../../../../../../ducks/selectors'
import { useStepperContext } from '../../../../../../../../hooks/useStepperContext'
import { Autocomplete, Grid, Typography } from '../../../../../../../UI'
import AccessDetails from '../../../components/AccessDetails'
import BottomButtons from '../../../components/BottomButtons'
import HomeownershipConfirmation from '../../../components/HomeownershipConfirmation'
import PropertyStatus from '../../../components/PropertyStatus'
import SchedulerContact from '../../../components/SchedulerContact'
import Scheduling from '../../../components/Scheduling'
import useStyles from './../../../styles'
import { EstimateType } from 'ducks/estimate/types'

const Step1Homeowner: FC = () => {
  const classes = useStyles()
  const [stepPayAtClose, setStepPayAtClose] = useState(false)
  const { setShowWarnings, setCanGoForward, showWarnings, saveNewValue } =
    useStepperContext()
  const goBack = history.useGoBack()
  const dispatch = useAppDispatch()
  const push = history.usePushForward()
  const estimate = useSelector(getEstimate())
  const userEmail = useSelector(getUserPrimaryEmail)
  const user = useSelector(getUser)
  const approvalStep = useSelector(getApprovalStep)
  const alreadyUpdatedListingAgreement = estimate?.properties?.files?.find(
    (file) => file.fileType === FILE_TYPE.LISTING_AGREEMENT
  )
  const alreadyUpdatedComparableMarketAnalysis =
    estimate?.properties?.files?.find(
      (file) => file.fileType === FILE_TYPE.COMPARABLE_MARKET_ANALYSIS
    )

  const contactInEstimateIndex = useSelector(
    getContactIndexInEstimate(userEmail)
  )

  const {
    preferredCompletedOn,
    preferredStartDate,
    lockboxConsent,
    schedulerSelect,
    propertyToBeSold,
    scheduler,
    propertyUnderContract,
    closingDate,
    propertyListed,
    completedBeforeClosing,
    paymentOption,
    lockbox,
    accessDetails,
    role,
    agentIsHomeowner,
    homeownershipConfirmation,
    isOccupied,
    hasChildrenUnderAge,
    hasPets,
  } = useSelector(getApprovalValues())

  const contactInEstimate = useSelector(getContactInEstimate(userEmail))
  const territory = useSelector(getTerritory())


  const agentContact = estimate?.properties?.contacts?.find(
    (c) =>
      companyRoles.includes(c.role as CONTACT_ROLE) && c.email !== userEmail
  )

  const schedulerFromEstimateIndex = estimate?.properties?.contacts?.findIndex(
    (c) => c?.isScheduler
  )
  const schedulerFromEstimate = estimate?.properties?.contacts?.find(
    (c) => c?.isScheduler
  )

  const showPayAtCompletionFlow =
    paymentOption === 'pay_at_completion' || agentIsHomeowner

  const isHomeowner = agentIsHomeowner || contactInEstimate?.role === CONTACT_ROLE.HOMEOWNER

  const schedularContactValidation =
    schedulerSelect === 'other'
      ? !!scheduler?.role &&
      !!scheduler.firstName &&
      !!scheduler.lastName &&
      !!scheduler.email &&
      validateEmail(scheduler.email) &&
      !!scheduler.phone &&
      validatePhone(scheduler?.phone)
      : true

  const houseInformationValidation = () => {
    if (propertyToBeSold !== undefined) {
      if (propertyToBeSold) {
        if (propertyUnderContract !== undefined) {
          if (propertyUnderContract) {
            return (
              !isEmpty(closingDate?.toString()) &&
              completedBeforeClosing !== undefined &&
              completedBeforeClosing !== null
            )
          } else {
            return propertyListed !== undefined && propertyListed !== null
          }
        }
      } else {
        return true
      }
    }
  }

  const disableNext = !!estimate?.properties.contacts?.find(
    (contact) => contact.approvalFlowInProgress && contact.email !== userEmail
  )

  useEffect(() => {
    if (paymentOption === "pay_at_completion" && stepPayAtClose) {
      setStepPayAtClose(false)
    }
  }, [paymentOption, stepPayAtClose])


  const occupiedDetailsValidation =
    isOccupied !== true ||
    (hasPets !== undefined &&
      hasPets !== null &&
      hasChildrenUnderAge !== undefined &&
      hasChildrenUnderAge !== null)

  const flowValidation =
    typeof preferredStartDate !== 'undefined' &&
    typeof preferredCompletedOn !== 'undefined' &&
    (lockbox || !!lockboxConsent) &&
    schedularContactValidation &&
    houseInformationValidation() &&
    homeownershipConfirmation &&
    isOccupied !== undefined &&
    isOccupied !== null &&
    occupiedDetailsValidation



  const validate = () => {
    if (disableNext) return false
    return flowValidation
  }

  useEffect(() => {
    setShowWarnings(false)
  }, [])

  useEffect(() => {
    setCanGoForward(validate() as boolean)
  }, [validate])

  const submitStep = () => {
    dispatch(
      configActions.setConfigValue({ type: 'overlayLoading', value: true })
    )
    updateEstimate()
  }

  const updateEstimate = () => {
    let valuesToSend: Partial<EstimateProperties> = {
      preferredStartDate,
      preferredCompletedOn,
      closingDate,
      completedBeforeClosing,
      propertyToBeSold,
      lockbox: lockbox ?? '',
      accessDetails,
      smsConsent: true,
      lockboxConsent,
      homeownershipConfirmation: homeownershipConfirmation,
      isOccupied,
      hasChildrenUnderAge,
      hasPets,
      approvalStep: approvalStep + 1,
    }

    if (propertyToBeSold) {
      valuesToSend = {
        ...valuesToSend,
        propertyUnderContract,
        closingDate,
        completedBeforeClosing,
        propertyListed,
      }
    } else {
      valuesToSend = {
        ...valuesToSend,
        propertyUnderContract: false,
        propertyListed: false,
        closingDate: null,
      }
    }

    const contactsPayload: UpdateRequest = []

    contactsPayload.push({
      op: 'replace',
      path: `/properties/contacts/${contactInEstimateIndex}/approvalFlowInProgress`,
      value: true,
    })

    if (agentIsHomeowner) {
      // if agent is homeowner, we need to update the role
      contactsPayload.push({
        op: 'replace',
        path: `/properties/contacts/${contactInEstimateIndex}/role`,
        value: role,
      })
    }

    if (schedulerSelect === 'me') {
      // if the logged user is the scheduler we remove the scheduler from the estimate and add isScheduler to the user
      contactsPayload.push({
        op: 'replace',
        path: `/properties/contacts/${contactInEstimateIndex}/isScheduler`,
        value: true,
      })

      if (
        schedulerFromEstimateIndex !== -1 &&
        schedulerFromEstimate?.email !== userEmail
      ) {
        contactsPayload.push({
          op: 'replace',
          path: `/properties/contacts/${schedulerFromEstimateIndex}/isScheduler`,
          value: false,
        })
      }
    } else {
      if (schedulerFromEstimate?.id !== scheduler?.id) {
        //we remove the isScheduler flag from the previous scheduler
        contactsPayload.push({
          op: 'replace',
          path: `/properties/contacts/${schedulerFromEstimateIndex}/isScheduler`,
          value: false,
        })
      }
      // if other we need to remove the flag from the 'me' user if existed
      if (scheduler?.id) {
        const differences: any =
          schedulerFromEstimate && diff(schedulerFromEstimate, scheduler)

        const schedulerDifferences: UpdateRequest =
          getDifferenceBetweenObjects(
            differences,
            undefined,
            `properties/contacts/${schedulerFromEstimateIndex}`
          )

        if (!differences?.email) {
          contactsPayload.push(...schedulerDifferences)
        } else {
          delete scheduler.id
          delete scheduler.contactId
          //if the user is trying to edit the email for an already existing scheduler we need to remove the scheduler flag from the previous one and add the new contact
          contactsPayload.push({
            op: 'replace',
            path: `/properties/contacts/${schedulerFromEstimateIndex}/isScheduler`,
            value: false,
          })
          contactsPayload.push({
            op: 'add',
            path: `/properties/contacts/-`,
            value: { ...scheduler, isScheduler: true },
          })
        }
      } else {
        contactsPayload.push({
          op: 'add',
          path: `/properties/contacts/-`,
          value: { ...scheduler, isScheduler: true },
        })
      }
    }

    dispatch(
      estimateActions.updateEstimateByPath(
        [
          ...contactsPayload,
          ...objectToUpdateRequest(valuesToSend, '/properties/'),
        ],
        (succ, estimate) => {
          dispatch(
            configActions.setConfigValue({
              type: 'overlayLoading',
              value: false,
            })
          )
        }
      )
    )
  }

  const sendApproval = () => {
    dispatch(
      configActions.setConfigValue({ type: 'overlayLoading', value: true })
    )
    const contacts: EstimateContact[] = estimate?.properties?.contacts?.reduce(
      (acc: any, curr: Partial<EstimateContact>) => {
        if (curr.email === userEmail) {
          acc = [
            ...acc,
            {
              ...curr,
              approvalType: APPROVAL_TYPE.APPROVED,
              isPayer: true,
            },
          ]
        } else {
          acc = [...acc, curr]
        }
        return acc
      },
      []
    )
    const payload: Partial<EstimateType> = {
      id: estimate?.id,
      properties: {
        address: estimate?.properties?.address,
        accessDetails: accessDetails,
        preferredCompletedOn: preferredCompletedOn,
        closingDate: closingDate,
        contacts: contacts,
        userId: user?.id,
        clientNotes: estimate?.properties?.clientNotes,
        totalValue: estimate?.properties?.totalValue ?? 0,
        approvalStep: 0,
      },
    }
    dispatch(
      estimateActions.approval(
        payload,
        (success: boolean, status: string) => {
          dispatch(
            configActions.setConfigValue({
              type: 'overlayLoading',
              value: false,
            })
          )
          if (success) {
            push(`success`)
          }
        }
      )
    )
  }

  const buttonText = () => {
    if (propertyToBeSold) {
      return 'Sign Service Agreement'
    }
    return 'Terms of Service'
  }

  return (
    <Grid className={classes.container} spacing={2}>
      {!stepPayAtClose && <HomeownershipConfirmation />}
      {agentIsHomeowner && (
        <Grid item xs={12} lg={4}>
          <Typography
            variant="body2"
            className={classes.question}
            style={{ marginTop: '24px' }}
          >
            What is your role in this transaction?
          </Typography>
          <Autocomplete
            label=""
            error={showWarnings && isEmpty(role)}
            value={
              contactInEstimate?.role === CONTACT_ROLE.AGENT_HOMEOWNER
                ? getContactRoleOpt().find(
                  (o) => o.key === CONTACT_ROLE.HOMEOWNER
                )
                : getContactRoleOpt().find((o) => o.key === role)
            }
            options={getContactRoleOpt()}
            onChange={(value: any) => {
              saveNewValue('role', value.key)
            }}
          />
        </Grid>
      )}


      {!stepPayAtClose &&
        (agentContact ||
          estimate?.properties?.propertyToBeSold ||
          agentIsHomeowner) && (
          <Grid
            style={{
              background: '#EAF2FE',
              borderRadius: '8px',
              padding: '1rem',
              border: '1px solid #ACCAFC',
            }}
          >
            <PropertyStatus isHomeowner={isHomeowner} />
          </Grid>
        )}

      {showPayAtCompletionFlow && (
        <Grid item>
          <Scheduling isHomeowner={isHomeowner} />
        </Grid>
      )}

      {showPayAtCompletionFlow && (
        <>
          {(paymentOption !== 'pay_at_close' || agentIsHomeowner) && (
            <SchedulerContact
              // value={scheduler || 'other'}
              showWarnings={showWarnings}
            />
          )}
          <AccessDetails />
        </>
      )}

      <BottomButtons
        buttonBack={{
          title: 'Back to Estimate',
          action: () => {
            goBack()
          },
        }}
        showNextButton
        buttonNext={{
          title: buttonText(),
          action: submitStep,
          disable: !validate(),
        }}
      />
    </Grid>
  )
}

export default Step1Homeowner
